// pages/admin/payroll/TerminationPayroll.jsx

import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  getPayrollRunsByType,
  lockPayrollRun,
  completePayRun,
  savePayrollDraft,
} from "@/services/payroll.service";
import { UserX, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDateTimeSeparate } from "@/lib/utils";
import { PayrollRunTable } from "./PayrollRunTable";
import { PayrollSheet } from "@/components/admin/payroll/PayrollSheet";
import { deletePayrollRun } from "@/services/payroll.service";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { createCanHelpers } from "@/utils/rbac";
import { usePermission } from "@/contexts/RoleAccessContext";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const TerminationPayroll = () => {
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [openSheet, setOpenSheet] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  
  useEffect(() => {
    loadRuns();
  }, []);

  /* --------------------------------------------
     LOAD TERMINATION PAYROLL RUNS
  ---------------------------------------------*/
  const loadRuns = async () => {
    try {
      const data = await getPayrollRunsByType("termination");
      setRuns(data || []);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load termination payroll runs"));
      }
      setRuns([]);
    }
  };

  /* --------------------------------------------
     OPEN RUN
  ---------------------------------------------*/
  const handleOpenRun = (run) => {
    if (!canEdit("run_payroll", true)) return;
    setActiveRun(run);
    setSelectedEmployees(run.employees.map((e) => e.employee_id));
  };

  /* --------------------------------------------
     LOCK PAYROLL (STAY ON RUN)
  ---------------------------------------------*/
  const handleLock = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
    await lockPayrollRun(activeRun._id, activeRun.employees);
    setActiveRun((prev) => ({ ...prev, status: "locked" }));
    await loadRuns();
    toast.success("Final settlement payroll locked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to lock final settlement payroll");
    }
  };

  /* --------------------------------------------
     COMPLETE PAYROLL (EXIT)
  ---------------------------------------------*/
  const handleComplete = async () => {
    if (!canEdit("run_payroll", true)) return;
    try {
    await completePayRun(activeRun._id);
    await loadRuns();
    setActiveRun(null);
    toast.success("Final settlement payroll completed successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to complete final settlement payroll");
    }
  };

  const handleEditEmployee = (emp) => {
    if (!canEdit("run_payroll", true)) return;
    setSelectedEmployee(emp);
    setOpenSheet(true);
  };

  const handleDeletePayrollRun = async () => {
    if (!canEdit("run_payroll", true)) return;
    if (!activeRun?._id) return;

    try {
      await deletePayrollRun(activeRun._id);

      toast.success("Termination payroll deleted successfully");

      // FULL RESET (same pattern as regular payroll)
      setActiveRun(null);
      setSelectedEmployees([]);
      setOpenSheet(false);
      setSelectedEmployee(null);

      await loadRuns(); // reload list
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete termination payroll");
    }
  };

  /* --------------------------------------------
     LIST VIEW
  ---------------------------------------------*/
  if (!activeRun) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-600" />
            Final Settlement Payrolls
          </CardTitle>
        </CardHeader>

        <CardContent>
          {runs.length === 0 ? (
            <div className="flex flex-col items-center py-14 text-center">
              <UserX className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">
                No Pending Final Settlements
              </h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">
                All terminated employees have been settled, or no terminations
                exist for this period.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Settlement Date</th>
                  <th className="text-left p-2">Period</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Action</th>
                </tr>
              </thead>

              <tbody>
                {runs.map((run) => {
                  const emp = run.employees[0];
                  const overdue = run.termination_info?.is_overdue;

                  return (
                    <tr
                      key={run._id}
                      className={`border-b`}
                    >
                      <td className="p-2">
                        <div className="font-medium">{emp.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {emp.empId}
                        </div>
                        {overdue && (
                          <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                            <AlertTriangle className="w-3 h-3" />
                            Settlement overdue
                          </div>
                        )}
                      </td>

                      <td className="p-2">
                        {formatDateTimeSeparate(
                          run.termination_info?.final_settlement_date
                        )?.date || "--"}
                      </td>

                      <td className="p-2">
                        {run.period_month}/{run.period_year}
                      </td>

                      <td className="p-2">
                        <Badge
                          variant={
                            overdue
                              ? "destructive"
                              : run.status === "locked"
                              ? "warning"
                              : "secondary"
                          }
                        >
                          {overdue ? "OVERDUE" : run.status.toUpperCase()}
                        </Badge>
                      </td>

                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant={overdue ? "destructive" : "default"}
                          onClick={() => handleOpenRun(run)}
                          disabled={!canEdit("run_payroll")}
                        >
                          {run.status === "locked"
                            ? "Disburse Now"
                            : "Continue"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    );
  }

  /* --------------------------------------------
     RUN VIEW
  ---------------------------------------------*/
  return (
    <>
      {/* OVERDUE WARNING */}
      {activeRun.termination_info?.is_overdue ? (
        <Card className="border-l-4 mb-4">
          <CardContent className="py-3 flex justify-between items-center gap-3">
            <div className="flex gap-4 items-center">
              <AlertTriangle className="w-5 h-5 mt-0.5 text-red-700" />
              <div>
                <p className="font-semibold">Final Settlement Overdue</p>
                <p className="text-xs ">
                  Settlement date was{" "}
                  <strong>
                    {
                      formatDateTimeSeparate(
                        activeRun.termination_info.final_settlement_date,
                      ).date
                    }
                  </strong>
                  . Please complete immediately to remain compliant.
                </p>
              </div>
            </div>

            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={!canEdit("run_payroll")}
            >
              Delete Payroll
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-l-4 mb-4">
          {/* TERMINATION BANNER */}
          <CardContent className="py-3 flex items-center justify-between">
            <div>
              <p className="font-semibold">Final Settlement Payroll</p>
              <p className="text-xs">Generated due to employee termination.</p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Payroll
            </Button>
          </CardContent>
        </Card>
      )}

      <PayrollRunTable
        employees={activeRun.employees}
        selectedEmployees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        payrollStatus={activeRun.status}
        showDaysWorked
        onLock={handleLock}
        onComplete={handleComplete}
        onEditEmployee={handleEditEmployee}
        isTermination
      />

      <PayrollSheet
        openSheet={openSheet}
        setOpenSheet={setOpenSheet}
        selectedEmployee={selectedEmployee}
        is_termination={true}
        periodMonth={
          activeRun?.status === "draft" ? activeRun?.period_month : undefined
        }
        periodYear={
          activeRun?.status === "draft" ? activeRun?.period_year : undefined
        }
        onSavePayroll={async (updatedEmployee) => {
          setActiveRun((prev) => ({
            ...prev,
            employees: prev.employees.map((emp) =>
              emp.employee_id === updatedEmployee.employee_id
                ? updatedEmployee
                : emp,
            ),
          }));

          // Persist the edit so it survives a refresh (a completed run is frozen).
          if (activeRun?.status === "completed" || !activeRun?._id) return;
          try {
            await savePayrollDraft(activeRun._id, [updatedEmployee]);
            toast.success("Changes saved");
          } catch (error) {
            toast.error("Failed to save changes");
          }
        }}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Termination Payroll?"
        description="This will permanently delete the final settlement payroll."
        message="This action cannot be undone. Employee settlement data will be lost."
        confirmText="Delete Payroll"
        cancelText="Cancel"
        destructive
        onConfirm={async () => {
          await handleDeletePayrollRun();
        }}
      />
    </>
  );
};

export default TerminationPayroll;
